import React, { FC, useEffect, useReducer, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store/rootReducer';
import { useSnackbar } from 'notistack';
import { errorVariant, infoVariant, successVariant, warningVariant } from '../../lib/barVariant';
import { IFindIdParams } from '../../models/auth.model';
import { doConfirmCodeForId, doConfirmCodeForPassword, doFindIdList, doGenerateCodeForId, doGenerateCodeForPassword } from '../../store/find.slice';
import { resetVerifyTimer, setCertifyKey, setCertifyValue, setIsVerify, setVerify, startTimer, stopTimer } from '../../store/verify.slice';
import Timer from './Timer';
import { Button, Select } from 'antd';
import { ACCOUNT_STATUS_TYPE } from '../../pages/auth/login';
import Loader from '../Loader';
import Spinner from '../Spinner';

const { Option } = Select;

interface IProps {
  verifyType: ACCOUNT_STATUS_TYPE
  handleNextPage?: any;
}


const Verify: FC<IProps> = ({ 
  verifyType, 
  handleNextPage,
 }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const history = useHistory();
  const dispatch = useDispatch();
  const { data: verifyData, timer, isLoading: isVerifyLoading } = useSelector((state: RootState) => state.verify);
  const { isLoading } = useSelector((state: RootState) => state.find);
  const { token, certifyKey, certifyValue, isExpired, status, webId, memberName } = verifyData;

  const [isDisabled1, setIsDisabled1] = useState(false);
  const [isDisabled2, setIsDisabled2] = useState(false);
  const [showPasswordInput, setShowPasswordInput] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [nationalCode, setNationalCode] = useState('');
  const [isTooltipHover, setIsTooltipHover] = useState(false);

  const VERIFY_TIMER_SECOND = 180;


  const nationalCodeOptions = [
    { label: '+82', value: '+82' }
  ]


  useEffect(()=> {
    setNationalCode('+82');
  }, [])


  const onSetStatus = (_status: 'SM' | 'EM') => {
    dispatch(
      setVerify({
        status: _status,
        certifyValue: '',
        token: '',
        certifyKey: '',
        isExpired: false,
        webId: webId,
        memberName: memberName,
        value: ''
      }
    ));
    dispatch(resetVerifyTimer());
    dispatch(setIsVerify(false));
    setIsDisabled1(false);
  }

  const checkNullFindId = () => {

    if (!certifyValue) {
      return `${status === 'SM' ? t('auth.findId.message7') : t('auth.findId.message8')}`;
    }
    const regex = /^\d+$/;
    if (status === 'SM' && !regex.test(certifyValue)) {
      return t('auth.findId.message7');
    }

    return false;
  }

  const checkNullFindPw = () => {
    if (!webId || webId === '') {
      return t('auth.findPassword.message10');
    }
    if (!memberName || memberName === '') {
      return t('auth.findPassword.message11');
    }
    if (!certifyValue || certifyValue === '') {
      return `${status === 'SM' ? t('auth.findId.message7') : t('auth.findId.message8')}`;
    }
    const regex = /^\d+$/;
    if (status === 'SM' && !regex.test(certifyValue)) {
      return t('auth.findPassword.message8');
    }

    return false;
  
  }

  const resetTimer = async () => {
    await dispatch(stopTimer());
    await dispatch(startTimer())
  }

  const checkNullForGenerateCode = (verifyType: ACCOUNT_STATUS_TYPE) => {
    switch(verifyType) {
      case ACCOUNT_STATUS_TYPE.LOGIN:
        return checkNullFindId();
      case ACCOUNT_STATUS_TYPE.FIND_ID:
        return checkNullFindId();
      case ACCOUNT_STATUS_TYPE.FIND_PASSWORD:
        return checkNullFindPw();
    }
  }

  const onGenerateCode = async () => {
    const checkNullMessage = checkNullForGenerateCode(verifyType);

    if(checkNullMessage) {
      return enqueueSnackbar(checkNullMessage, infoVariant);
    } 
    
    switch (verifyType) {
        case ACCOUNT_STATUS_TYPE.LOGIN: {
          const res = await dispatch(doGenerateCodeForId({
            value: certifyValue,
            status: status,
            sendType: 'LG'
          }));
    
          switch (res?.status) {
            case 200:
              enqueueSnackbar(t('auth.findId.message2'), successVariant);
              setIsDisabled1(true);
              dispatch(
                setVerify({ ...verifyData, token: res.data.data })
              );
              timer.status
                ? resetTimer()
                : dispatch(startTimer());
              break;
            case 400:
              enqueueSnackbar(
                status === 'SM' 
                  ? t('auth.findId.message9')
                  : t('auth.findId.message10'), 
                infoVariant);
              break;
            default:
              enqueueSnackbar(res?.data?.data || t('common.message.error'), errorVariant);
          }
          break;
        }
        case ACCOUNT_STATUS_TYPE.FIND_ID: {
          const res = await dispatch(doGenerateCodeForId({
            value: certifyValue,
            status: status,
            sendType: 'ID'
          }));
    
          switch (res?.status) {
            case 200:
              enqueueSnackbar(t('auth.findId.message2'), successVariant);
              setIsDisabled1(true);
              dispatch(
                setVerify({ ...verifyData, token: res.data.data })
              );
              timer.status
                ? resetTimer()
                : dispatch(startTimer());
              break;
            case 400:
              enqueueSnackbar(
                status === 'SM' 
                  ? t('auth.findId.message9')
                  : t('auth.findId.message10'), 
                infoVariant);
              break;
            default:
              enqueueSnackbar(res?.data?.data || t('common.message.error'), errorVariant);
          }
          break;
        }

        case ACCOUNT_STATUS_TYPE.FIND_PASSWORD: {
          const res = await dispatch(doGenerateCodeForPassword({
            status,
            webId: webId|| '',
            memberName: memberName || '',
            value: certifyValue,
            sendType: 'PW'
          }));
      
          switch (res?.status) {
            case 200:
              enqueueSnackbar(t('auth.findId.message2'), successVariant);
              setIsDisabled1(true);
              dispatch(
                setVerify({ ...verifyData, token: res.data.data })
              );
              timer.status
                ? resetTimer()
                : dispatch(startTimer());
              break;
            case 400:
              enqueueSnackbar(
                status === 'SM' 
                  ? t('auth.findId.message9')
                  : t('auth.findId.message10'), 
                infoVariant);
              break;
            default:
              enqueueSnackbar(res?.data?.data || t('common.message.error'), errorVariant);
          }
        }
    }
  }

  const onConfirmCode = async () => {

    if(isExpired) {
      return enqueueSnackbar(
        [t('auth.verify.message1'), <br/>, t('auth.verify.message2')], 
        infoVariant
      );
    }

    switch(verifyType) {
      case ACCOUNT_STATUS_TYPE.LOGIN: {
        if (!certifyKey) {
          return enqueueSnackbar(t('auth.findId.message4'), infoVariant);
        }
        
        const res = await dispatch(doConfirmCodeForId({
            token,
            tokenTarget: certifyValue,
            certifyKey,
        }));
    
        switch (res?.status) {
          case 200:
            dispatch(stopTimer());
            dispatch(setIsVerify(true));
            break;
          case 400:
            enqueueSnackbar(t('auth.findId.message6'), infoVariant);
            break;
          default:
            enqueueSnackbar(res?.data?.data || t('common.message.error'), errorVariant);
        }

        break;
      }

      case ACCOUNT_STATUS_TYPE.FIND_ID: {
        if (!certifyKey) {
          return enqueueSnackbar(t('auth.findId.message4'), infoVariant);
        }
        
        const res = await dispatch(doConfirmCodeForId({
            token,
            tokenTarget: certifyValue,
            certifyKey,
        }));
    
        switch (res?.status) {
          case 200:
            setIsDisabled2(true);
            dispatch(stopTimer());
            await findMemberId();
            handleNextPage();
            break;
          case 400:
            enqueueSnackbar(t('auth.findId.message6'), infoVariant);
            break;
          default:
            enqueueSnackbar(res?.data?.data || t('common.message.error'), errorVariant);
        }

        break;
      }

      case ACCOUNT_STATUS_TYPE.FIND_PASSWORD: {
        if (!certifyKey) {
          return enqueueSnackbar(t('auth.findId.message4'), infoVariant);
        }
    
        const res = await dispatch(doConfirmCodeForPassword({
            token,
            tokenTarget: certifyValue,
            certifyKey
        }));
    
        switch (res?.status) {
          case 200:
            setIsDisabled2(true);
            dispatch(stopTimer());
            handleNextPage();
            break;
          case 400:
            enqueueSnackbar(t('auth.findPassword.message7'), infoVariant);
            break;
          default:
            enqueueSnackbar(res?.data?.data || t('common.message.error'), errorVariant);
        }

        break;
      }
    }
  }

  const findMemberId = async () => {
    const req: IFindIdParams = { }; 
    if (status === 'SM') req.mobilePhone = certifyValue;
    else req.email = certifyValue;

    const res = await dispatch(doFindIdList(req));

    switch (res?.status) {
      case 200:
        break;
      default:
        enqueueSnackbar(res?.data?.data || t('common.message.error'), errorVariant);
    }
  }

  const onSetNationalCode = (nationalCode: string) => {
    setNationalCode(nationalCode);
  }

  return (
    <>
      {(
        <div className="tab-area">
          <div className="tab-menu">
            <button
              type="button"
              className={`btn-phone ${status === 'SM' && 'active'}`}
              onClick={() => onSetStatus('SM')}
            >
              휴대폰
            </button>
            <button
              type="button"
              className={`btn-mail ${status === 'EM' && 'active'}`}
              onClick={() => onSetStatus('EM')}
            >
              {t('common.text.eMail')}
            </button>
          </div>
          <div className="tab-cont">
            <div
              className={`tooltip-area ${isTooltipHover && 'active'}`}
              onMouseOver={() => setIsTooltipHover(true)}
              onMouseLeave={() => setIsTooltipHover(false)}
            >
              <div className="cont">
                {t('auth.findId.text8')}
              </div>
              <button type="button" className="btn"></button>
            </div>
            <div className="bx-row d-flex">
              {
                status === 'SM' && (
                  <Select
                    onChange={onSetNationalCode}
                    placeholder={t('class.class.placeholder7')}
                    value={nationalCode}
                    className="mr-2"
                  >
                    {nationalCodeOptions?.map((option, index) =>
                      <Select.Option
                        key={`${option.value}-${index}`}
                        value={option.value}
                      >
                        {option.label}
                      </Select.Option>
                    )}
                  </Select>
                )
              }
              <div className="bx-input">
                <input
                  type="text"
                  placeholder={status === 'SM' ? t('auth.findId.placeholder4') : t('homepage.member.9')}
                  onChange={(e) => dispatch(setCertifyValue(e.target.value))}
                  disabled={isDisabled1}
                  value={certifyValue}
                />
                <Button className="btn-send"
                        onClick={onGenerateCode}
                        loading={isLoading}
                >
                  {t('auth.findId.button1')}
                </Button>
              </div>
            </div>


            <div className="bx-row">
              <div className="bx-input">
                <input
                  type="number"
                  placeholder={t('auth.findId.placeholder3')}
                  value={certifyKey}
                  onChange={(e) => dispatch(setCertifyKey(e.target.value))}
                  disabled={isDisabled2}
                />
                <span className="limit">
                  {
                    timer.status ? (
                      <Timer
                        initSec={VERIFY_TIMER_SECOND}
                      />
                    ) : undefined
                  }
                </span>
              </div>
            </div>
            <div className="btn-area">
              <button type="button"
                      className="btn-basic pink"
                      disabled={isLoading}
                      onClick={onConfirmCode}
              >
                {t('auth.findId.button2')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default Verify;
